import { useState, useMemo, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { Loader2, X } from 'lucide-react'
import api from '@/lib/api/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getAnalyticsHex, AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { appLocale } from '@/lib/dateUtils'

// Estate-wide per-day rollup. Threats/analytics and anomalies are DIFFERENT signals shown on
// SEPARATE channels (like the per-object Analytics History modal): the day's scan disposition
// is the column BACKGROUND, the anomaly count is BARS — so neither hides the other.
interface TimelineDay {
    day: string
    threats: number      // versions scanned confirmed-malicious (analytics = 2) — red
    suspicious: number   // versions with indicators (analytics = 3 or 5) — amber
    clean: number        // versions scanned no-threats-detected (analytics = 4) — green
    anomalies: number    // versions with a behavioural anomaly (anomaly_score > 0)
}

// One flagged version on the selected day (drill-down detail behind a column).
interface DayItem {
    id: number
    objectID: string
    objectName: string
    vaultID: string
    datetime: string | null
    analytics: number | null
    anomalyScore: number
    flags: string[]      // any of 'threat' | 'indicator' | 'anomaly'
    kinds: string[]      // for threats: malware / ransomware / config
}
interface DayDetail {
    date: string
    counts: { threats: number; indicators: number; anomalies: number; objects: number }
    items: DayItem[]
}

const RANGE_OPTIONS = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
] as const

// The day's overall scan disposition (worst wins) drives the column background band —
// 2 compromised, 3 suspicious, 4 clean, 0 not scanned.
function dispositionCode(d: TimelineDay): number {
    if (d.threats > 0) return 2
    if (d.suspicious > 0) return 3
    if (d.clean > 0) return 4
    return 0
}

const ANOMALY_BAR = '#475569'

// Coloured pill for a disposition flag in the detail table.
function FlagPill({ flag, kinds }: { flag: string; kinds?: string[] }) {
    const map: Record<string, { bg: string; label: string }> = {
        threat: { bg: getAnalyticsHex(2), label: 'Threat' },
        indicator: { bg: getAnalyticsHex(3), label: 'Indicator' },
        anomaly: { bg: ANOMALY_BAR, label: 'Anomaly' },
    }
    const c = map[flag] || { bg: '#94a3b8', label: flag }
    const text = flag === 'threat' && kinds?.length ? `${c.label} · ${kinds.join(', ')}` : c.label
    return (
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium text-white whitespace-nowrap" style={{ background: c.bg }}>
            {text}
        </span>
    )
}

export function TimelineTab() {
    const { t } = useTranslation()
    const [days, setDays] = useState(30)
    const [selectedDay, setSelectedDay] = useState<string | null>(null)

    const { data: rows = [], isLoading } = useQuery({
        queryKey: ['analytics', 'timeline', days],
        queryFn: async () => {
            const res = await api.get('/api/analytics/timeline', { params: { days } })
            return (res.data?.data || res.data || []) as TimelineDay[]
        },
    })

    const hasData = rows.length > 0

    // The API only returns days that had scan/anomaly activity, so quiet days would collapse
    // and the x-axis would misrepresent time. Fill EVERY calendar day in the selected window
    // (empty = zero) so the timeline is continuous and evenly spaced.
    const filled = useMemo<TimelineDay[]>(() => {
        if (!rows.length) return []
        const byDay = new Map(rows.map((r) => [r.day, r]))
        const todayStr = new Date().toISOString().slice(0, 10)
        const maxDay = rows.reduce((m, r) => (r.day > m ? r.day : m), rows[0].day)
        const end = new Date(`${maxDay > todayStr ? maxDay : todayStr}T00:00:00Z`)
        const cursor = new Date(end)
        cursor.setUTCDate(cursor.getUTCDate() - (days - 1))
        const out: TimelineDay[] = []
        for (let i = 0; i < days; i++) {
            const key = cursor.toISOString().slice(0, 10)
            out.push(byDay.get(key) || { day: key, threats: 0, suspicious: 0, clean: 0, anomalies: 0 })
            cursor.setUTCDate(cursor.getUTCDate() + 1)
        }
        return out
    }, [rows, days])

    // Keep the latest day list reachable from the (once-attached) canvas click handler.
    const filledRef = useRef<TimelineDay[]>([])
    filledRef.current = filled

    const selectedIndex = useMemo(
        () => (selectedDay ? filled.findIndex((r) => r.day === selectedDay) : -1),
        [filled, selectedDay]
    )

    const chartOption = useMemo((): EChartsOption => {
        if (!hasData) return {}

        const labels = filled.map((r) => {
            const dt = new Date(`${r.day}T00:00:00`)
            return isNaN(dt.getTime()) ? r.day : dt.toLocaleDateString(appLocale(), { month: 'short', day: 'numeric' })
        })

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const arr = Array.isArray(params) ? params : [params]
                    const i = arr[0]?.dataIndex
                    const r = filled[i]
                    if (!r) return ''
                    let html = `<div style="font-weight:600;margin-bottom:4px">${labels[i]}</div>`
                    html += `<div>Anomalies: <b>${r.anomalies}</b></div>`
                    html += `<div style="margin-top:2px;color:#666;font-size:11px">Scan: ${r.threats} confirmed · ${r.suspicious} indicators · ${r.clean} no-threat</div>`
                    html += `<div style="margin-top:3px;color:#3b82f6;font-size:10px">Click to see the day's detail</div>`
                    return html
                },
            },
            grid: { left: 40, right: 20, top: 20, bottom: 68 },
            // Date-range scrubber under the chart — matches the per-object analytics charts.
            dataZoom: [
                { type: 'inside' },
                { type: 'slider', height: 18, bottom: 8, showDataShadow: false },
            ],
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: { fontSize: 10, color: '#888', rotate: 35 },
                axisTick: { show: false },
                axisLine: { show: false },
            },
            yAxis: {
                type: 'value',
                name: t('analytics.anomalies', 'Anomalies'),
                minInterval: 1,
                nameTextStyle: { fontSize: 10, color: '#888' },
                axisLabel: { fontSize: 10, color: '#888' },
                splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
            },
            series: [
                {
                    type: 'bar',
                    name: t('analytics.anomalies', 'Anomalies'),
                    data: filled.map((r) => r.anomalies || 0),
                    itemStyle: { color: ANOMALY_BAR, borderRadius: [3, 3, 0, 0] },
                    barMaxWidth: 28,
                    cursor: 'pointer',
                    // Full-height background band per day, coloured by that day's scan
                    // disposition (the analytics dimension) — drawn behind the anomaly bars so
                    // the two never hide each other.
                    markArea: {
                        silent: true,
                        data: filled.map((r, i) => [
                            { xAxis: i - 0.5, itemStyle: { color: getAnalyticsHex(dispositionCode(r)), opacity: 0.5 } },
                            { xAxis: i + 0.5 },
                        ]),
                    },
                    // Highlight the day currently drilled into.
                    markLine: selectedIndex >= 0 ? {
                        silent: true,
                        symbol: 'none',
                        lineStyle: { color: '#3b82f6', width: 2 },
                        label: { show: false },
                        data: [{ xAxis: selectedIndex }],
                    } : undefined,
                },
            ],
        }
    }, [filled, hasData, selectedIndex, t])

    // Click anywhere in a day's column (bar OR the disposition band, incl. quiet days) to
    // drill in. We map the click pixel to the nearest category via the chart's own scale, so
    // it stays correct under the range scrubber. Clicking the same day again clears it.
    const handleReady = useCallback((chart: any) => {
        const zr = chart.getZr?.()
        if (!zr) return
        zr.off('click')
        zr.on('click', (event: any) => {
            const pt = [event.offsetX, event.offsetY]
            if (!chart.containPixel({ gridIndex: 0 }, pt)) return
            const conv = chart.convertFromPixel({ gridIndex: 0 }, pt)
            const idx = Math.round(conv?.[0])
            const list = filledRef.current
            if (!Number.isInteger(idx) || idx < 0 || idx >= list.length) return
            const day = list[idx].day
            setSelectedDay((cur) => (cur === day ? null : day))
        })
    }, [])

    const { data: detail, isFetching: detailLoading } = useQuery({
        queryKey: ['analytics', 'timeline-day', selectedDay],
        enabled: !!selectedDay,
        queryFn: async () => {
            const res = await api.get('/api/analytics/timeline/day', { params: { date: selectedDay } })
            return (res.data?.data || res.data) as DayDetail
        },
    })

    const selectedLabel = useMemo(() => {
        if (!selectedDay) return ''
        const dt = new Date(`${selectedDay}T00:00:00`)
        return isNaN(dt.getTime())
            ? selectedDay
            : dt.toLocaleDateString(appLocale(), { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
    }, [selectedDay])

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div>
                    <CardTitle>{t('analytics.timeline', 'Anomaly Timeline')}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Background = scan disposition · bars = anomalies — across the estate, per day
                    </p>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: getAnalyticsHex(4) }} />No threats</span>
                        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: getAnalyticsHex(3) }} />Indicators</span>
                        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: getAnalyticsHex(2) }} />Confirmed</span>
                        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: ANOMALY_BAR }} />Anomalies</span>
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {RANGE_OPTIONS.map((opt) => (
                        <button
                            key={opt.days}
                            type="button"
                            onClick={() => { setDays(opt.days); setSelectedDay(null) }}
                            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                                days === opt.days
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'border-border text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Loading timeline...
                    </div>
                ) : !hasData ? (
                    <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                        No scan or anomaly data in the selected period.
                    </div>
                ) : (
                    <>
                        <ReactECharts
                            option={chartOption}
                            style={{ height: 400 }}
                            opts={{ renderer: 'canvas' }}
                            onChartReady={handleReady}
                        />
                        <p className="text-[11px] text-muted-foreground text-center -mt-1">
                            Drag the range slider to zoom · click a day to see its detail below
                        </p>

                        {selectedDay && (
                            <div className="mt-4 border-t pt-4">
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div>
                                        <h4 className="text-sm font-semibold">{selectedLabel}</h4>
                                        {detail && (
                                            <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                                                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: getAnalyticsHex(2) }} />{detail.counts.threats} threats</span>
                                                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: getAnalyticsHex(3) }} />{detail.counts.indicators} indicators</span>
                                                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: ANOMALY_BAR }} />{detail.counts.anomalies} anomalies</span>
                                                <span className="text-muted-foreground/70">across {detail.counts.objects} object{detail.counts.objects === 1 ? '' : 's'}</span>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedDay(null)}
                                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground shrink-0"
                                    >
                                        <X className="h-3.5 w-3.5" /> Clear
                                    </button>
                                </div>

                                {detailLoading ? (
                                    <div className="flex items-center justify-center h-[120px] text-muted-foreground">
                                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                        Loading detail...
                                    </div>
                                ) : !detail?.items.length ? (
                                    <div className="flex items-center justify-center h-[100px] text-sm text-muted-foreground">
                                        No flagged versions on this day.
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto rounded-md border">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                                                    <th className="px-3 py-2 font-medium">Object</th>
                                                    <th className="px-3 py-2 font-medium">Disposition</th>
                                                    <th className="px-3 py-2 font-medium">Scan status</th>
                                                    <th className="px-3 py-2 font-medium text-right">Anomaly</th>
                                                    <th className="px-3 py-2 font-medium">Vault</th>
                                                    <th className="px-3 py-2 font-medium">Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.items.map((it) => {
                                                    const timeStr = it.datetime
                                                        ? new Date(it.datetime).toLocaleTimeString(appLocale(), { hour: '2-digit', minute: '2-digit' })
                                                        : '—'
                                                    return (
                                                        <tr key={it.id} className="border-b last:border-0 hover:bg-muted/30">
                                                            <td className="px-3 py-2">
                                                                <div className="font-medium">{it.objectName}</div>
                                                                {it.objectName !== it.objectID && (
                                                                    <div className="text-[11px] text-muted-foreground">{it.objectID}</div>
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {it.flags.map((f) => (
                                                                        <FlagPill key={f} flag={f} kinds={it.kinds} />
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                {it.analytics != null
                                                                    ? <AnalyticsStatusIndicator status={it.analytics} showLabel showIcon />
                                                                    : <span className="text-muted-foreground">—</span>}
                                                            </td>
                                                            <td className="px-3 py-2 text-right tabular-nums">
                                                                {it.anomalyScore > 0 ? it.anomalyScore : <span className="text-muted-foreground">—</span>}
                                                            </td>
                                                            <td className="px-3 py-2 text-muted-foreground">{it.vaultID || '—'}</td>
                                                            <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{timeStr}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    )
}
